/**
 * WHAT THE PLAYER CHOSE BEFORE PRESSING PLAY. One owner.
 *
 * 🚨 **EVERY MATCH EVER PLAYED IN THIS CLIENT USED `Decks[0]`.** Alex, 2026-08-14:
 *
 * > *"did you import our 4 decks of cards? are you sure the game rules, and format, and flow are
 * > correct? It still feels wrong"*
 *
 * The decks were imported and they were fine: four starter decks on the server, thirty cards each,
 * one per realm hero. **The client simply never sent one.** `Lobby/PracticeStartSession` accepts a
 * `deckId`, the client posted `{}`, and `MatchRegistry.cs:215` reads:
 *
 * ```cs
 * var humanDeck = _data.FindDeck(humanDeckId) ?? _data.Decks[0];
 * ```
 *
 * So the same deck came up every single time, no matter which hero the player thought they were.
 * That is why it felt wrong - not the rules, the FORMAT: a card game where you do not pick your
 * deck is not the game.
 *
 * ⛔ **A SILENT DEFAULT IS THE DEFECT, NOT THE SAFETY NET** (`engineering-invariants` §3b). The
 * fallback is still there so the tutorial can start without a picker, but the client now always
 * chooses, and `verify_deck_choice.py` fails if a match is started without one.
 */

export interface Choice {
  leagueId?: string | undefined;
  leagueName?: string | undefined;
  deckId?: string | undefined;
  deckName?: string | undefined;
  /**
   * The bot skill the player picked for a PRACTICE match.
   *
   * ⭐ Practice is the one mode where difficulty is chosen rather than earned. Everywhere else a
   * ladder decides it, so this is only ever read on the practice path.
   */
  difficulty?: "BankedFire" | "Normal" | "Hard" | undefined;
  /**
   * A SPECIAL RULE to play practice under, e.g. `TheLongDark`.
   *
   * ⭐ A special encounter is one persistent rule on an otherwise normal match, so practising under
   * one needs no new game logic - `MatchSession.ApplySpecialRule` already runs whatever the context
   * names, on every turn change, in any mode. It was simply only ever reached from a campaign stage.
   *
   * ⚠️ The SERVER validates it against the rules it implements and refuses an unknown one. A rule
   * carried into a match that matches nothing plays as an ordinary game while claiming not to.
   */
  specialRule?: string | undefined;
}

/**
 * ⚠️ Module state, deliberately - it is the CURRENT choice, and there is exactly one player. It is
 * not persisted: a deck choice is per-sitting, and reviving a stale one across a reload would start
 * a match with a deck the player last saw days ago.
 */
let current: Choice = {};

export function setChoice(next: Choice): void {
  current = { ...current, ...next };
}

export function choice(): Choice {
  return current;
}

export function clearChoice(): void {
  current = {};
}
